import React from 'react'
import WorkWeek from '../work-week'
import IntlMessages from '../../util/IntlMessages'
import { getAverageWorkingHours, getRotationDates, userHasRotatingSchedule, userHasWorkingHoursSchedule } from '@vacationtracker/shared/functions/work-week'
import { dayNames } from '@vacationtracker/shared/types/calendar'
import { IWorkingSchedule, WorkingHours } from '@vacationtracker/shared/types/work-week'
import { Space, Table } from 'antd'
import { useIntl } from 'react-intl'
import capitalize from 'lodash/capitalize'
import dayjs from 'dayjs'

interface IUserWorkingHours {
  firstDayOfWeek: number
  workingHours: WorkingHours | IWorkingSchedule
}

const UserWorkingDaysAndHours = ({ firstDayOfWeek, workingHours }: IUserWorkingHours): React.ReactElement => {
  const { formatMessage } = useIntl()

  const columns = [
    {
      title: capitalize(formatMessage({ id: 'app.day' })),
      dataIndex: 'day',
      key: 'day',
    },
    ...(userHasRotatingSchedule(workingHours)
      ? (workingHours as IWorkingSchedule).workHours.map((_, index) => {
          const { weekStart, weekEnd } = getRotationDates(workingHours as IWorkingSchedule, index)
          const dateFormat = 'MMM D'
          return {
            title: `${dayjs(weekStart).format(dateFormat)} - ${dayjs(weekEnd).format(dateFormat)}`,
            dataIndex: `week${index}`,
            key: `week${index}`,
          }
        })
      : [
          {
            title: capitalize(formatMessage({ id: 'app.workingHours' })),
            dataIndex: 'workingHours',
            key: 'workingHours',
          },
        ]),
  ]

  const getTableData = (dayHours: WorkingHours | IWorkingSchedule) => {
    if (userHasRotatingSchedule(dayHours)) {
      const daysData = Array.from({ length: 7 }, (_, dayIndex) => {
        const rowData: any = {
          key: dayIndex,
          day: <IntlMessages id={dayNames[dayIndex]} />,
        }

        // Add data for each week
        const schedule = dayHours as IWorkingSchedule
        schedule.workHours.forEach((weekHours, weekIndex) => {
          const dayHour = (weekHours as any).find(([day]) => day === dayIndex)
          rowData[`week${weekIndex}`] = dayHour ? dayHour[1] : '-'
        })

        return rowData
      })

      // Handle first day of week
      if (firstDayOfWeek === 1 && daysData[0]) {
        const sunday = daysData.shift()
        if (sunday) daysData.push(sunday)
      }

      return daysData
    }

    const columns = (dayHours as WorkingHours)
      .map(([day, hours]) => ({
        key: day,
        day: <IntlMessages id={dayNames[day]} />,
        workingHours: hours,
      }))
      .sort((a, b) => a.key - b.key)

    if (firstDayOfWeek === 1 && columns[0]?.key === 0) {
      const sunday = columns.shift()
      if (sunday) columns.push(sunday)
    }

    return columns
  }

  return userHasWorkingHoursSchedule(workingHours) ? (
    <Space style={{ padding: '0 24px 24px' }}>
      <Table dataSource={getTableData(workingHours)} columns={columns} pagination={false} size="small" />
    </Space>
  ) : (
    <>
      <div style={{ padding: 25 }}>
        <WorkWeek days={(workingHours as unknown as WorkingHours).map(([day]) => day)} firstDayOfWeek={firstDayOfWeek} separator=", " />
      </div>
      <div style={{ padding: '0 25px 25px 25px' }}>
        <IntlMessages id="app.workday" />: &nbsp;{getAverageWorkingHours(workingHours)} <IntlMessages id="app.hours" />
      </div>
    </>
  )
}

export default UserWorkingDaysAndHours
